import { redactEmails } from '@/lib/redact-emails';

const GOOGLE_TAG_ID = 'AW-18004391561';
const GOOGLE_TAG_SCRIPT_ID = 'cleanlist-google-tag';

type GoogleTagWindow = Window & {
  dataLayer?: IArguments[];
  gtag?: (...args: unknown[]) => void;
};

/**
 * The page fields Google reads, with emails stripped. Person pages live at
 * /people/<email>, and gtag otherwise sends the raw URL (dl=) on config and
 * on every event, so a direct load of one sent the lead's email to Google.
 */
export function redactedGooglePage(
  targetWindow: Window = window,
  targetDocument: Document = document
): { page_location: string; page_referrer: string } {
  return {
    page_location: redactEmails(targetWindow.location.href),
    page_referrer: redactEmails(targetDocument.referrer),
  };
}

/**
 * True when the page's own URL carries an email (/people/<email>). gtag also
 * sends `top=`, the top frame's URL, read from the browser and not settable,
 * so redacting page_location isn't enough: such a page must not load the tag.
 */
export function pageHasEmail(targetWindow: Window = window): boolean {
  const href = targetWindow.location.href;
  return redactEmails(href) !== href;
}

/**
 * Load Google Ads only after the synchronous Slack callback scrub in main.tsx.
 * Keeping this out of index.html prevents callback fragments from being visible
 * to third-party scripts before the application entry module can remove them.
 */
export function initGoogleTag(
  targetWindow: Window = window,
  targetDocument: Document = document
): void {
  if (targetDocument.getElementById(GOOGLE_TAG_SCRIPT_ID)) return;
  // Ads only measures sign-up and checkout, which never land on a person page.
  if (pageHasEmail(targetWindow)) return;

  const googleWindow = targetWindow as GoogleTagWindow;
  googleWindow.dataLayer ??= [];
  googleWindow.gtag = function gtag() {
    // gtag.js only processes queued commands pushed as the Arguments object
    // (the official snippet's `dataLayer.push(arguments)`), not as a plain
    // Array, so this deliberately does not use rest parameters.
    // eslint-disable-next-line prefer-rest-params
    googleWindow.dataLayer?.push(arguments);
  };
  googleWindow.gtag('js', new Date());
  // Before config: 'set' applies to every command after it.
  googleWindow.gtag('set', redactedGooglePage(targetWindow, targetDocument));
  googleWindow.gtag('config', GOOGLE_TAG_ID);

  const script = targetDocument.createElement('script');
  script.id = GOOGLE_TAG_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TAG_ID}`;
  targetDocument.head.appendChild(script);
}
